import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import css from '../SkipTraceModal/style.scss';
import { closePopup, getPopupRegistration, openPopup, Priority } from 'app/PopupHolder';
import infoGray from 'assets/images/info-gray.svg';
import { useToast } from '../ToastMessage/ToastManager';
import Tooltip from '../../components/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import ListManagementField from 'components/ListManagementField';
import {
  selectCardList,
  selectListManagementEnabled,
  selectPrepaidCredits,
  selectUserId,
  selectPlanDetails,
  isLegacyAccountPlan,
  selectShouldPromotePropStreamConnect,
} from '../../../data/user';
import { selectInvestorGroups } from '../../../data/contacts';
import {
  appendContacts,
  checkSkipTraced,
  getAppendJobEstimate,
  selectAppendJob,
  selectLoading,
} from '../../../data/contacts';
import InfoAlert from '../InfoAlert';
import { saveInvestorGroup } from '../../../data/investors';
import { toInvestorContactRequest } from '../../../data/investors/investorUtils';
import NewCreditCardDropdown from '../NewCreditCardDropdown';
import DNCPolicyModal from '../DNCPolicyModal';
import { isCardExpired } from '../../../utils/validation';
import eyeOpen from 'assets/images/eye-on.svg';
import eyeClosed from 'assets/images/eye-off.svg';
import SVG from 'components/base/SVG';
import SimpleModal from 'components/base/SimpleModal';
import infoAlertCss from '../InfoAlert/style.scss';
import AddPaymentPopup from '../../AccountNew/modals/AddPayment';

const BODY_CLASS_NAME = 'bodyModal';

// ATAI-468: POST appendjobs returns as soon as the investor job is created --
// propstream-be's executeInvestorSkipTrace hands the vendor call and the
// contact_phones/contact_emails writes to an @Asynchronous method
// (processInvestorSkipTraceAsync, ATAI-226). A refresh fired straight off that
// response re-reads the investor before any contacts exist, so the Investor
// Profile (and search cards / Investor List rows) kept showing "Skip Trace"
// until reopened. Poll check-skip-traced -- which only counts a contact once
// its job is COMPLETE -- and refresh only after every contact is done.
//
// Short wait: the server caps the vendor call at 25s, so this covers it with
// room to spare. Long wait (>100 investors, user already told "we'll email
// you") matches the async job's own 15 minute transaction timeout.
const SKIP_TRACE_WAIT = { intervalMs: 2000, timeoutMs: 60000 };
const SKIP_TRACE_LONG_WAIT = { intervalMs: 10000, timeoutMs: 15 * 60 * 1000 };

// Calls onDone(true) once every contact's job is COMPLETE, or onDone(false)
// at the timeout. A failed check just tries again on the next tick; the
// deadline timer is separate so a request the API middleware drops without
// a callback still can't leave the caller waiting forever.
const waitForSkipTrace = (dispatch, contactIds, { intervalMs, timeoutMs }, onDone) => {
  let finished = false;
  let pollTimer = null;

  const finish = (completed) => {
    if (finished) return;
    finished = true;
    clearTimeout(pollTimer);
    clearTimeout(deadlineTimer);
    onDone(completed);
  };

  const deadlineTimer = setTimeout(() => finish(false), timeoutMs);

  const scheduleNext = () => {
    if (!finished) pollTimer = setTimeout(poll, intervalMs);
  };

  function poll() {
    dispatch(
      checkSkipTraced(
        { contactIds },
        ({ response }) => {
          // Read skipTracedContacts, not nonSkipTracedContacts: propstream-be
          // omits empty lists from the JSON, so "nothing pending" arrives as
          // the field missing entirely rather than as [].
          const traced = new Set((response?.skipTracedContacts || []).map(String));
          if (contactIds.every((id) => traced.has(String(id)))) {
            finish(true);
          } else {
            scheduleNext();
          }
        },
        scheduleNext
      )
    );
  }

  scheduleNext();
};

// Skip Trace for investors (ATAI-115 / S9) — Figma node 658:77933. A separate
// component from the classic SkipTraceModal rather than a shared-file edit:
// the two only look similar. The classic modal's "Name Your List" field
// doesn't touch Investor Lists at all -- it just names the skip-trace job
// record itself (ContactGroupTypes.APPEND, propstream-be ContactAppendService).
// Figma's "Add to Investor List" is the real S10 feature (ContactGroupTypes.
// INVESTOR, saveInvestorGroup) -- a genuinely different action. Editing the
// shared modal to fit both would risk the homeowner-contact flow every other
// Skip Trace entry point still depends on; this component instead reuses that
// modal's billing/payment/eligibility logic verbatim and only replaces the
// list field + labels.
//
// Place Order therefore fires two calls, not one: saveInvestorGroup (the real
// Investor List add) then, on its success, appendContacts (the skip-trace job
// itself, billing/eligibility unchanged) -- both dedupe server-side on the
// same materialized contact, so their order relative to each other is safe.
//
// `investors` -- the raw investor search-result objects (for
// toInvestorContactRequest / saveInvestorGroup); empty when opened from a
// context that only has already-materialized contacts (see `initialGroup`
// below). `contactIds` -- already materialized (ATAI-113/S7) before this
// modal opens, same as the classic flow's SkipTraceHandler -- see
// ../../InvestorOutreach/useSkipTraceInvestors.
//
// Three entry points, three ways the list field starts:
//  - Investor Search (`investors` populated, no lockedGroup/initialGroup):
//    field starts empty, saveInvestorGroup runs for whatever's picked.
//  - Contacts -> Investor List's own Skip Trace button (`initialGroup` set,
//    `investors` empty): field starts on the current list, editable, but
//    picking a *different* list isn't wired yet -- see handlePlaceOrder.
//  - AddToInvestorListModal's "Skip Trace Selected Investors" checkbox
//    (`lockedGroup` set): field shows that list, disabled.
const InvestorSkipTraceModal = ({
  title = 'Skip Trace',
  closePopup,
  contactIds,
  investors = [],
  // Set when the list was already decided by whatever opened this modal
  // (AddToInvestorListModal's "Skip Trace Selected Investors" checkbox,
  // chained after its own save) -- the field shows that list, disabled:
  // re-picking here doesn't make sense right after choosing it there.
  lockedGroup,
  // Set when opened from *inside* an Investor List already (Contacts ->
  // Investor List's own Skip Trace button) -- pre-fills the field with that
  // list, but leaves it editable (Contacts-page contacts already have a
  // contactId; the field starts on their current list, not empty).
  initialGroup,
  onSuccess,
  onCancel,
  // Passed by useSkipTraceInvestors so a caller inside another popup can
  // restore it when this one bails out before ever showing.
  onClose,
}) => {
  const modalRef = useRef(null);
  const showToast = useToast();
  const cardList = useSelector(selectCardList);
  const dispatch = useDispatch();
  const _userId = useSelector(selectUserId);
  const _planDetails = useSelector(selectPlanDetails)?.toJS();
  const _isLegacyAccount = useSelector(isLegacyAccountPlan);
  const _shouldPromotePropStreamConnect = useSelector(selectShouldPromotePropStreamConnect);
  const investorGroups = useSelector(selectInvestorGroups);

  const [selectedList, setSelectedList] = useState(lockedGroup || initialGroup || null);
  const [selectedCard, setSelectedCard] = useState(cardList[0] || {});
  const [cvv, setCvv] = useState('');
  const [cvvVisible, setCvvVisible] = useState(false);
  const [cvvError, setCvvError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [noEligibleOpen, setNoEligibleOpen] = useState(false);
  // DNC Policy renders inline, not via DNCPolicyModal.open(): both are HIGHEST, and
  // PopupHolder only renders the front of a priority queue, so it would queue
  // behind this modal and never show.
  const [dncPolicyOpen, setDncPolicyOpen] = useState(false);
  // Place Order stays disabled until this open's own estimate lands, so the
  // eligibility check never reads a previous job's leftover store data.
  const [estimateReady, setEstimateReady] = useState(false);

  const appendJobState = useSelector(selectAppendJob);
  const appendJob =
    appendJobState && typeof appendJobState.toJS === 'function'
      ? appendJobState.toJS()
      : appendJobState || {};
  const listManagementEnabled = useSelector(selectListManagementEnabled);
  const loading = useSelector(selectLoading);
  const prepaidInfoState = useSelector(selectPrepaidCredits);
  const prepaidInfo =
    prepaidInfoState && typeof prepaidInfoState.toJS === 'function'
      ? prepaidInfoState.toJS()
      : prepaidInfoState || {};

  const validateCVV = (value) => {
    const requiredLength = selectedCard?.cardCVVLength ? selectedCard?.cardCVVLength : 3;

    if (!value) return 'CVV is required';
    if (!/^\d+$/.test(value)) return 'CVV must contain only numbers';
    if (value.length !== requiredLength) return `CVV must be ${requiredLength} digits`;

    return '';
  };

  const handleCvvChange = (e) => {
    const value = e.target.value;

    if (!/^\d*$/.test(value)) return;

    setCvv(value);
    setCvvError(validateCVV(value));
  };

  const handleCardChange = (selectedOption) => {
    setSelectedCard(selectedOption);
  };

  const handleClose = ({ cancelled } = {}) => {
    document.body.classList.remove(BODY_CLASS_NAME);
    if (cancelled) onCancel?.();
    closePopup?.();
  };

  const AddPaymentMethod = () => {
    dispatch(AddPaymentPopup.open());
  };

  useEffect(() => {
    document.body.classList.add(BODY_CLASS_NAME);

    const previouslyFocusedElement = document.activeElement;
    modalRef.current?.focus();

    const getFocusableElements = () => {
      if (!modalRef.current) return [];
      return Array.from(
        modalRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
    };

    const keydownHandler = (e) => {
      if (e.key === 'Escape') {
        handleClose({ cancelled: true });
        return;
      }

      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const isInsideModal = modalRef.current.contains(document.activeElement);

      if (e.shiftKey) {
        if (!isInsideModal || document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!isInsideModal || document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const popstateHandler = () => handleClose({ cancelled: true });

    document.addEventListener('keydown', keydownHandler, true);
    window.addEventListener('popstate', popstateHandler);

    return () => {
      document.removeEventListener('keydown', keydownHandler, true);
      window.removeEventListener('popstate', popstateHandler);
      document.body.classList.remove(BODY_CLASS_NAME);
      previouslyFocusedElement?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the estimate on every open, whatever entry point opened this modal
  // (Search card/bulk/Profile, Contacts list, Add to Investor List chaining).
  // The modal only reads `appendJob` from the store, so any caller that
  // skipped its own estimate call left stale data behind and Place Order
  // wrongly reported "No Eligible Investors" (ATAI-474). investorMode routes
  // to the investor estimate path server-side (ATAI-226).
  useEffect(() => {
    if (!contactIds?.length) return;

    dispatch(
      getAppendJobEstimate(
        { contactIds, appendMode: 'BOTH', reprocessEnabled: false, investorMode: true },
        ({ response, error }) => {
          if (error) {
            console.warn('Error fetching skip trace estimate:', error);
            showToast('Could not load the Skip Trace estimate. Please try again.', 'error');
            onClose?.();
            handleClose({ cancelled: true });
            return;
          }

          if (response?.message?.includes('There is an existing append job')) {
            dispatch(
              InfoAlert.open({
                title: 'Skip Trace in Progress',
                infoText:
                  'There is a Skip Trace currently in progress. Please try again once it is complete.',
              })
            );
            onClose?.();
            handleClose({ cancelled: true });
            return;
          }

          setEstimateReady(true);
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDNCPolicyModal = () => {
    setDncPolicyOpen(true);
  };

  const runSkipTraceJob = (listLabel) => {
    const paymentMethodId = selectedCard?.paymentMethodId;
    const cardCode = cvv ? cvv : null;
    const lines = appendJob.lines || [];
    const paymentAmount = appendJob?.total || 0;
    const creditAmount = appendJob?.credits || 0;
    const calculatedRate = appendJob?.total === 0 ? 0 : appendJob?.rate;
    const { rate: _rate, ...appendJobWithoutRates } = appendJob;

    const payload = {
      ...appendJobWithoutRates,
      reprocessEnabled: false,
      lines,
      name: listLabel,
      rate: calculatedRate,
      investorMode: true,
    };

    const eligibleCount = appendJob?.newQuantity ?? 0;

    if (eligibleCount > 100) {
      showToast(
        "Skip Trace in progress. This may take a few minutes — we'll email you when it's ready.",
        'success'
      );
      handleClose();
      dispatch(
        appendContacts(
          { ...payload, contactIds },
          paymentMethodId,
          cardCode,
          paymentAmount,
          creditAmount,
          ({ error }) => {
            if (error) {
              showToast(
                'Something went wrong with your Skip Trace order. Please check your email or try again.',
                'error'
              );
              return;
            }

            // Modal is already closed -- refresh the caller in the background
            // once the job has actually written its contacts (ATAI-468).
            waitForSkipTrace(dispatch, contactIds, SKIP_TRACE_LONG_WAIT, () => onSuccess?.());
          }
        )
      );
      return;
    }

    dispatch(
      appendContacts(
        { ...payload, contactIds },
        paymentMethodId,
        cardCode,
        paymentAmount,
        creditAmount,
        ({ error }) => {
          if (error) {
            setIsSubmitting(false);
            showToast(
              "Something went wrong and your order wasn't placed. Please try again.",
              'error'
            );
            handleClose();
            return;
          }

          // The order is placed but the job is still running server-side
          // (ATAI-468) -- stay on "Processing..." until its contacts are
          // written, so the success toast and the caller's refresh are both
          // real.
          waitForSkipTrace(dispatch, contactIds, SKIP_TRACE_WAIT, (completed) => {
            setIsSubmitting(false);
            showToast(
              completed
                ? 'Skip Trace completed successfully.'
                : 'Skip Trace is still processing. Results will appear shortly.',
              'success'
            );
            onSuccess?.();
            handleClose();
          });
        }
      )
    );
  };

  const handlePlaceOrder = () => {
    if (isSubmitting || !selectedList || !estimateReady) return;
    if (appendJob?.newQuantity <= 0) {
      setNoEligibleOpen(true);
      return;
    }

    const listLabel = selectedList.name || selectedList;
    const listUnchanged =
      (lockedGroup || initialGroup) &&
      String(selectedList.id ?? selectedList.name) ===
        String((lockedGroup || initialGroup).id ?? (lockedGroup || initialGroup).name);

    // Already a member of this list (locked, or picked from the Contacts
    // page without changing it) -- nothing to add, go straight to the job.
    if (listUnchanged) {
      setIsSubmitting(true);
      runSkipTraceJob(listLabel);
      return;
    }

    // Opened from the Contacts page (already-materialized contacts, no raw
    // `investors` payload available here) and the user picked a *different*
    // list than the one they started on. Moving between lists needs its own
    // safe implementation (see this component's header) -- not wired yet,
    // so refuse rather than send saveInvestorGroup an empty investors array.
    if (initialGroup && !investors.length) {
      showToast(
        "Moving investors to a different list from here isn't supported yet — please use Add to Investor List instead.",
        'error'
      );
      return;
    }

    setIsSubmitting(true);

    const isCreated = !selectedList.id;

    dispatch(
      saveInvestorGroup(
        {
          investors: investors.map(toInvestorContactRequest),
          ...(isCreated
            ? { name: listLabel }
            : { groupId: Number(String(selectedList.id).replace(/^\D+/, '')) }),
        },
        () => runSkipTraceJob(listLabel),
        () => {
          setIsSubmitting(false);
          showToast('Could not save to the Investor List. Please try again.', 'error');
        }
      )
    );
  };

  return (
    <div className={css.modalOverlay}>
      <div className={css.modalWrapper}>
        <div ref={modalRef} tabIndex={-1} className={classNames(css.modalBox, 'modal-content')}>
          <div className={css.modal_header}>{title}</div>

          <div className={css.modal_body}>
            {/* LEFT SIDE */}
            <div className={css.leftCol}>
              <ListManagementField
                lists={investorGroups}
                label="Add to Investor List"
                showLabel
                required
                defaultValue={lockedGroup || initialGroup}
                disabled={Boolean(lockedGroup)}
                onSelect={(list) => setSelectedList(list)}
                onCreate={(name) => setSelectedList({ name })}
                placeholder="Select or Type to Create a New List"
              />

              <div className={css.orderInfo}>
                Your card is authorized for all selected records, and refunded for any without
                contact info. By clicking &ldquo;Place Order&rdquo; you agree to the
                <a
                  target="_blank"
                  href="https://www.propstream.com/terms-of-use"
                  className={css.highlight}
                  rel="noreferrer"
                >
                  {' '}
                  PropStream Terms and Conditions
                </a>
                , and
                <span className={css.highlight} onClick={openDNCPolicyModal}>
                  {' '}
                  DNC Policy
                </span>
                .
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className={css.rightCol}>
              <div className={css.orderBox}>
                {loading && (
                  <div className={css.loadingOverlay}>
                    <SVG icon="spinner" className={classNames(css.mainspinner)} />
                  </div>
                )}
                <div className={css.orderTitle}>Order Details</div>
                <div className={css.orderDetails}>
                  <div className={css.orderRow}>
                    <span>Selected Investors</span>
                    <span>{contactIds?.length}</span>
                  </div>

                  <div className={css.orderRow}>
                    <span>
                      Eligible Investors
                      <Tooltip
                        title="Eligible Investors"
                        description="Some investors will be omitted if they are duplicates, already Skip Traced, or missing information."
                        position="bottom"
                        bgColor="#128292"
                        width={312}
                        xOffset={125}
                        yOffset={2}
                        arrowOffset={30}
                      >
                        <img src={infoGray} alt="info" className={css.eligibleinfoIcon} />
                      </Tooltip>
                    </span>
                    <span>{appendJob?.newQuantity}</span>
                  </div>

                  <div className={css.orderRow}>
                    <span>Price Per Match</span>
                    <span>${Number(appendJob?.rate ?? 0).toFixed(2)}</span>
                  </div>

                  <div className={classNames(css.orderRow, css.subtotalRow)}>
                    <span>Subtotal</span>
                    <span>${Number(appendJob?.subtotal ?? 0).toFixed(2)}</span>
                  </div>

                  {listManagementEnabled ? (
                    <div className={classNames(css.orderRow)}>
                      <span>Free Skip Trace Credits</span>
                      <span>-${Number(appendJob?.freeSkipTraceCredit ?? 0).toFixed(2)}</span>
                    </div>
                  ) : null}

                  {!listManagementEnabled && prepaidInfo?.remainingPrepaidCredit ? (
                    <div className={classNames(css.orderRow)}>
                      <span>Prepaid Credits</span>
                      <span>-${Number(appendJob?.prepaidcredits ?? 0).toFixed(2)}</span>
                    </div>
                  ) : null}

                  <div className={classNames(css.orderRow, css.totalRow)}>
                    <span>Total</span>
                    <span>${Number(appendJob?.total ?? 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {appendJob?.total > 0 && (
                <div className={css.paymentBox}>
                  <div className={css.paymentTitle}>Payment Method</div>

                  <NewCreditCardDropdown
                    tag={false}
                    options={cardList}
                    selectedCard={selectedCard}
                    onChange={handleCardChange}
                    AddPaymentMethod={AddPaymentMethod}
                  />

                  {appendJob?.total > 100 && (
                    <div className={css.cvvWrapper}>
                      <div className={css.cvvInputWrapper}>
                        <input
                          type={cvvVisible ? 'text' : 'password'}
                          maxLength={4}
                          placeholder="Enter CVV"
                          className={classNames(css.cvvInput, {
                            [css.cvvInputError]: cvvError,
                          })}
                          value={cvv}
                          onChange={handleCvvChange}
                          onBlur={() => setCvvError(validateCVV(cvv))}
                        />

                        <img
                          src={cvvVisible ? eyeOpen : eyeClosed}
                          alt="toggle visibility"
                          className={css.cvvToggle}
                          onClick={() => setCvvVisible((v) => !v)}
                        />
                      </div>

                      {cvvError && <div className={css.cvvErrorText}>{cvvError}</div>}
                      <div className={css.cvvNote}>
                        For your security, please re-enter the CVV for your saved card to complete
                        this purchase
                      </div>
                    </div>
                  )}

                  {selectedCard && isCardExpired(selectedCard?.expDate) && (
                    <div className={css.expCardMsg}>
                      Your card has expired. Please use another card.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className={css.modal_footer}>
            <button
              type="button"
              className={classNames(css.btn, css.btn_secondary)}
              onClick={() => handleClose({ cancelled: true })}
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={!selectedList || isSubmitting || !estimateReady}
              className={classNames(css.btn, css.btn_primary, {
                [css.disabledBtn]: !selectedList || isSubmitting || !estimateReady,
              })}
              onClick={handlePlaceOrder}
            >
              {isSubmitting ? 'Processing...' : 'Place Order'}
            </button>
          </div>
        </div>
      </div>
      {noEligibleOpen && (
        // Inside the overlay so it renders above Skip Trace; OK closes both.
        <SimpleModal
          title="No Eligible Investors Found"
          description="Sorry, none of the selected investors are eligible for Skip Trace. Please select different investors and try again."
          cancelText="OK"
          onCancel={() => handleClose()}
          ignoreEscAndClickOutside
          modalClassName={infoAlertCss.clsInfoAlert}
        />
      )}
      {dncPolicyOpen && <DNCPolicyModal closePopup={() => setDncPolicyOpen(false)} />}
    </div>
  );
};

const registrationId = getPopupRegistration(InvestorSkipTraceModal);
// HIGHEST, not MEDIUM: the Investor Profile modal's own "Skip Trace" button
// (ContactInfo.js) opens this while Profile (also MEDIUM) is already
// showing -- PopupHolder only ever renders the front of a priority queue, so
// a same-tier popup queues silently behind it and never appears. Same
// reasoning as EditContactModal.js and IdentityHeader's "Add to Investor
// List" button; harmless for this modal's other callers (Results/index.js,
// InvestorCard.js), which have nothing else open to queue behind anyway.
InvestorSkipTraceModal.open = (props = {}) =>
  openPopup(registrationId, { ...props, priority: Priority.HIGHEST });
InvestorSkipTraceModal.close = () => closePopup({ popup: registrationId });

export default InvestorSkipTraceModal;
